/**
 * IdentityCard — the "My profile" card (name / pronouns / cell phone /
 * language editor), first card of /settings/account (Plan 04; moved
 * off the retired /profile page per PROD-27).
 *
 * Debounced auto-save (600ms after the last keystroke) calls
 * `updateMyProfile` with only the fields that changed. Errors surface
 * via `toast` from `@ordo/ui/sonner`; the local draft state preserves
 * the un-saved value so the user can retry.
 *
 * Pronouns is a canonical `<Select>` with a fallback "Custom" option
 * that surfaces a free-form input. Cell phone is a masked `<Input>`
 * (`formatUsPhone`) that constrains input to digits and formats live as
 * "313-234-2344" (Sheila 7/21 QA, item 4); the dashed string is what we
 * persist. Locale is derived from `ORDO_LOCALES`; the field is a
 * preferred-for-comms hint, NOT the in-app UI locale — `User.locale` is a
 * stamped default on 31,286 of 31,290 rows, so the app resolves its UI
 * language from an explicit device choice and `navigator.language` instead.
 */

import { ORDO_LOCALES, type OrdoLocale } from '@ordo/i18n'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { FormField } from '@ordo/ui/form-field'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { toast } from '@ordo/ui/sonner'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type MeProfile, type UpdateMyProfileVariables, useUpdateMyProfileMutation } from '@/api/me'
import { formatUsPhone, isCompleteUsPhone } from '@/lib/phone'

const PRONOUN_OPTIONS = [
  { value: 'she/her', label: 'she/her' },
  { value: 'he/him', label: 'he/him' },
  { value: 'they/them', label: 'they/them' },
  { value: 'ze/zir', label: 'ze/zir' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  { value: 'custom', label: 'Custom…' },
] as const

/**
 * Derived from `ORDO_LOCALES` so the picker can never offer a language the
 * app cannot serve — it previously offered `fr_CA`, which no catalog, PDF or
 * email has ever had a translation for (0 users had selected it).
 *
 * Values keep the UNDERSCORE form this column has always stored: all 31,290
 * live rows are `en_US`/`es_US`, and `toOrdoLocale` normalizes on read, so
 * there is nothing to gain from writing a second format into shared data.
 */
const LOCALE_LABELS: Record<OrdoLocale, string> = {
  'en-US': 'English (US)',
  'es-US': 'Español (EE.UU.)',
}

const LOCALE_OPTIONS = ORDO_LOCALES.map((locale) => ({
  value: locale.replace('-', '_'),
  label: LOCALE_LABELS[locale],
}))

const CANONICAL_PRONOUNS: ReadonlySet<string> = new Set(
  PRONOUN_OPTIONS.filter((o) => o.value !== 'custom').map((o) => o.value),
)

const DEBOUNCE_MS = 600

interface IdentityDraft {
  firstName: string
  lastName: string
  preferredName: string
  /**
   * One of the canonical select values, or `'custom'` when the user
   * has typed a custom string (held in `pronounsCustom`).
   */
  pronounsSlot: string
  pronounsCustom: string
  phoneNumber: string
  locale: string
}

function deriveDraft(me: MeProfile): IdentityDraft {
  const pronouns = me.pronouns ?? ''
  const isCanonical = CANONICAL_PRONOUNS.has(pronouns)
  return {
    firstName: me.firstName ?? '',
    lastName: me.lastName ?? '',
    preferredName: me.preferredName ?? '',
    pronounsSlot: pronouns === '' ? '' : isCanonical ? pronouns : 'custom',
    pronounsCustom: pronouns === '' || isCanonical ? '' : pronouns,
    phoneNumber: me.phoneNumber ?? '',
    locale: me.locale ?? 'en_US',
  }
}

function resolvedPronouns(draft: IdentityDraft): string | null {
  if (draft.pronounsSlot === '') {
    return null
  }
  if (draft.pronounsSlot === 'custom') {
    const trimmed = draft.pronounsCustom.trim()
    return trimmed === '' ? null : trimmed
  }
  return draft.pronounsSlot
}

function diffForUpdate(draft: IdentityDraft, me: MeProfile): UpdateMyProfileVariables {
  const out: UpdateMyProfileVariables = {}
  if (draft.firstName !== me.firstName) {
    out.firstName = draft.firstName
  }
  if (draft.lastName !== me.lastName) {
    out.lastName = draft.lastName
  }
  const draftPref = draft.preferredName.trim() === '' ? null : draft.preferredName
  if (draftPref !== me.preferredName) {
    out.preferredName = draftPref
  }
  const draftPronouns = resolvedPronouns(draft)
  if (draftPronouns !== me.pronouns) {
    out.pronouns = draftPronouns
  }
  const trimmedPhone = draft.phoneNumber.trim()
  const draftPhone = trimmedPhone === '' ? null : trimmedPhone
  // Persist only a cleared field or a complete 10-digit number — never a
  // half-typed partial the backend's 6-char minimum would reject mid-edit.
  if (draftPhone !== me.phoneNumber && (draftPhone === null || isCompleteUsPhone(trimmedPhone))) {
    out.phoneNumber = draftPhone
  }
  if (draft.locale !== me.locale) {
    out.locale = draft.locale
  }
  return out
}

export interface IdentityCardProps {
  me: MeProfile
}

export function IdentityCard({ me }: IdentityCardProps) {
  const [draft, setDraft] = useState<IdentityDraft>(() => deriveDraft(me))
  const updateMutation = useUpdateMyProfileMutation()
  const meRef = useRef(me)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDraftRef = useRef<IdentityDraft | null>(null)

  // Re-seed the draft when the upstream `me` row changes (e.g. a
  // successful save). We do NOT re-seed while a debounce window is
  // open — the user may have typed more since the last persist —
  // so we only sync from upstream when no edit is pending.
  useEffect(() => {
    meRef.current = me
    if (pendingDraftRef.current === null) {
      setDraft(deriveDraft(me))
    }
  }, [me])

  const persist = useCallback(
    (next: IdentityDraft) => {
      const diff = diffForUpdate(next, meRef.current)
      if (Object.keys(diff).length === 0) {
        return
      }
      pendingDraftRef.current = next
      updateMutation
        .mutateAsync(diff)
        .then(() => {
          pendingDraftRef.current = null
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : 'Try again in a moment.'
          toast.error("Couldn't save your changes", { description: message })
          pendingDraftRef.current = null
        })
    },
    [updateMutation],
  )

  const scheduledPersist = useCallback(
    (next: IdentityDraft) => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current)
      }
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null
        persist(next)
      }, DEBOUNCE_MS)
    },
    [persist],
  )

  // Flush any pending debounce on unmount so an in-flight keystroke
  // doesn't get dropped when the user navigates away.
  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current)
        debounceTimer.current = null
      }
    }
  }, [])

  const update = useCallback(
    (partial: Partial<IdentityDraft>) => {
      setDraft((prev) => {
        const next = { ...prev, ...partial }
        scheduledPersist(next)
        return next
      })
    },
    [scheduledPersist],
  )

  const showCustomPronoun = draft.pronounsSlot === 'custom'

  const statusText = useMemo(() => {
    if (updateMutation.isPending) {
      return 'Saving…'
    }
    if (debounceTimer.current !== null) {
      return 'Saving…'
    }
    return null
  }, [updateMutation.isPending])

  return (
    <Card>
      <CardHeader>
        <CardTitle>My profile</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label="First name" htmlFor="profile-first-name" required>
            <Input
              id="profile-first-name"
              value={draft.firstName}
              onChange={(e) => update({ firstName: e.target.value })}
              autoComplete="given-name"
            />
          </FormField>
          <FormField label="Last name" htmlFor="profile-last-name" required>
            <Input
              id="profile-last-name"
              value={draft.lastName}
              onChange={(e) => update({ lastName: e.target.value })}
              autoComplete="family-name"
            />
          </FormField>
        </div>
        <FormField
          label="Preferred name"
          htmlFor="profile-preferred-name"
          helperText="Optional. How you'd like to be addressed in Ordo.">
          <Input
            id="profile-preferred-name"
            value={draft.preferredName}
            onChange={(e) => update({ preferredName: e.target.value })}
            autoComplete="nickname"
          />
        </FormField>
        <FormField label="Pronouns">
          <Select
            value={draft.pronounsSlot}
            onValueChange={(v) => update({ pronounsSlot: v, ...(v === 'custom' ? {} : { pronounsCustom: '' }) })}>
            <SelectTrigger>
              <SelectValue placeholder="Select pronouns" />
            </SelectTrigger>
            <SelectContent>
              {PRONOUN_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showCustomPronoun && (
            <Input
              className="mt-2"
              placeholder="Your pronouns"
              value={draft.pronounsCustom}
              onChange={(e) => update({ pronounsCustom: e.target.value })}
              maxLength={40}
            />
          )}
        </FormField>
        <FormField
          label="Cell phone"
          htmlFor="profile-cell-phone"
          helperText="We'll text you about meal cutoffs and unserved-meal flags.">
          <Input
            id="profile-cell-phone"
            type="tel"
            inputMode="tel"
            value={draft.phoneNumber}
            onChange={(e) => update({ phoneNumber: formatUsPhone(e.target.value) })}
            placeholder="313-234-2344"
            maxLength={12}
            autoComplete="tel"
          />
        </FormField>
        <FormField
          label="Language"
          helperText="The language we'll use in emails and texts. In-app translations will follow in a future release.">
          <Select value={draft.locale} onValueChange={(v) => update({ locale: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOCALE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        {statusText && <span className="text-xs text-muted-foreground">{statusText}</span>}
      </CardContent>
    </Card>
  )
}
